import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const BusinessRestockHistory = () => {
  const routeBase = useWorkspaceBasePath();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [selectedRestock, setSelectedRestock] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [filters, setFilters] = useState({
    date_debut: '',
    date_fin: ''
  });
  const printRef = useRef();

  useEffect(() => {
    loadHistory();
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/business/settings');
      setSettings(res.data);
    } catch (err) {
      console.error('Erreur chargement paramètres:', err);
    }
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      
      const res = await api.get(`/business/restock-history?${params.toString()}`);
      setHistory(res.data.history || []);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (restockId) => {
    try {
      setDetailsLoading(true);
      const res = await api.get(`/business/restock-history/${restockId}`);
      setSelectedRestock(res.data);
    } catch (err) {
      console.error('Erreur chargement détails:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleFilter = () => {
    loadHistory();
  };

  const handlePrint = () => {
    if (!selectedRestock) return;
    
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Ravitaillement ${selectedRestock.reference}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #6366f1; }
            .company-info { display: flex; gap: 15px; align-items: flex-start; }
            .company-logo { width: 70px; height: 70px; object-fit: contain; border-radius: 8px; }
            .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 5px; }
            .company-details { font-size: 12px; color: #6b7280; line-height: 1.6; }
            .doc-title { text-align: right; }
            .doc-title h1 { font-size: 22px; color: #6366f1; margin-bottom: 5px; }
            .doc-title .ref { font-size: 14px; font-weight: bold; color: #374151; }
            .doc-title .date { font-size: 12px; color: #6b7280; margin-top: 5px; }
            .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; display: flex; justify-content: space-around; text-align: center; }
            .summary-item { }
            .summary-value { font-size: 18px; font-weight: bold; color: #6366f1; }
            .summary-label { font-size: 11px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #6366f1; color: white; padding: 10px 8px; text-align: left; font-size: 11px; text-transform: uppercase; }
            td { padding: 10px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; }
            tr:nth-child(even) { background: #f9fafb; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; text-align: center; }
            .total-row { font-weight: bold; background: #eef2ff !important; }
          </style>
        </head>
        <body>
          ${printRef.current.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to={`${routeBase}/restock`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
          ←
        </Link>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">📜 Historique des ravitaillements</h1>
          <p className="text-gray-500 text-sm">Consultez l'historique de vos ravitaillements de stock</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
            <input
              type="date"
              value={filters.date_debut}
              onChange={(e) => setFilters({...filters, date_debut: e.target.value})}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
            <input
              type="date"
              value={filters.date_fin}
              onChange={(e) => setFilters({...filters, date_fin: e.target.value})}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <button
            onClick={handleFilter}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Filtrer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des ravitaillements */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <div className="bg-purple-600 text-white px-4 py-3">
              <h2 className="font-semibold">Ravitaillements ({history.length})</h2>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {history.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="text-4xl mb-2">📦</div>
                  <p>Aucun ravitaillement trouvé</p>
                </div>
              ) : (
                history.map(item => (
                  <button
                    key={item.id}
                    onClick={() => loadDetails(item.id)}
                    className={`w-full p-4 text-left hover:bg-purple-50 transition ${
                      selectedRestock?.id === item.id ? 'bg-purple-100 border-l-4 border-purple-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-semibold text-purple-700">{item.reference}</span>
                      <span className="text-xs text-gray-500">{formatDate(item.date_ravitaillement)}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {item.total_produits} produits • {item.total_unites} unités
                    </div>
                    <div className="text-sm font-semibold text-gray-800">
                      {parseFloat(item.cout_total).toLocaleString()} XOF
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Détails du ravitaillement sélectionné */}
        <div className="lg:col-span-2">
          {detailsLoading ? (
            <div className="bg-white rounded-xl shadow p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600"></div>
            </div>
          ) : selectedRestock ? (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="bg-purple-600 text-white px-4 py-3 flex justify-between items-center">
                <h2 className="font-semibold">Détails - {selectedRestock.reference}</h2>
                <button
                  onClick={handlePrint}
                  className="px-3 py-1 bg-white/20 rounded-lg hover:bg-white/30 text-sm flex items-center gap-1"
                >
                  🖨️ Imprimer
                </button>
              </div>
              
              {/* Résumé */}
              <div className="bg-purple-50 p-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-purple-700">{selectedRestock.total_produits}</div>
                  <div className="text-xs text-gray-600">Produits</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-700">{selectedRestock.total_unites}</div>
                  <div className="text-xs text-gray-600">Unités</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-700">{parseFloat(selectedRestock.cout_total).toLocaleString()}</div>
                  <div className="text-xs text-gray-600">Coût total (XOF)</div>
                </div>
              </div>

              {/* Informations */}
              <div className="p-4 border-b bg-gray-50">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Date:</span>{' '}
                    <span className="font-medium">{formatDate(selectedRestock.date_ravitaillement)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Référence:</span>{' '}
                    <span className="font-medium text-purple-700">{selectedRestock.reference}</span>
                  </div>
                  {selectedRestock.expense_id && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Décaissement:</span>{' '}
                      <span className="font-medium text-green-600">✓ Enregistré (ID: {selectedRestock.expense_id})</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Tableau des produits */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Produit</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock avant</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Qté ajoutée</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Stock après</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Prix unit.</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Coût</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedRestock.items?.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">{item.product_nom}</div>
                          {item.product_code && (
                            <div className="text-xs text-gray-500">{item.product_code}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{item.stock_avant}</td>
                        <td className="px-4 py-3 text-center font-semibold text-green-600">+{item.quantite}</td>
                        <td className="px-4 py-3 text-center font-semibold text-purple-700">{item.stock_apres}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{parseFloat(item.prix_achat).toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium">{parseFloat(item.cout_ligne).toLocaleString()} XOF</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-purple-50">
                    <tr>
                      <td colSpan="5" className="px-4 py-3 text-right font-semibold">TOTAL</td>
                      <td className="px-4 py-3 text-right font-bold text-purple-700">
                        {parseFloat(selectedRestock.cout_total).toLocaleString()} XOF
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedRestock.notes && (
                <div className="p-4 border-t bg-gray-50">
                  <div className="text-sm text-gray-500">Notes:</div>
                  <div className="text-gray-700">{selectedRestock.notes}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow p-12 text-center">
              <div className="text-6xl mb-4">👈</div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">Sélectionnez un ravitaillement</h3>
              <p className="text-gray-500">Cliquez sur un ravitaillement à gauche pour voir les détails</p>
            </div>
          )}
        </div>
      </div>

      {/* Contenu imprimable (caché) */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {selectedRestock && (
            <>
              {/* En-tête entreprise */}
              <div className="header">
                <div className="company-info">
                  {settings?.logo && (
                    <img 
                      src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                      alt="Logo"
                      className="company-logo"
                    />
                  )}
                  <div>
                    <div className="company-name">{settings?.nom_entreprise || 'Mon Entreprise'}</div>
                    <div className="company-details">
                      {settings?.adresse && <div>{settings.adresse}</div>}
                      {settings?.telephone && <div>📞 {settings.telephone}</div>}
                      {settings?.email && <div>📧 {settings.email}</div>}
                      {settings?.numero_registre && <div>RCCM: {settings.numero_registre}</div>}
                      {settings?.numero_fiscal && <div>NIF: {settings.numero_fiscal}</div>}
                    </div>
                  </div>
                </div>
                <div className="doc-title">
                  <h1>BON DE RAVITAILLEMENT</h1>
                  <div className="ref">{selectedRestock.reference}</div>
                  <div className="date">{formatDate(selectedRestock.date_ravitaillement)}</div>
                </div>
              </div>

              {/* Résumé */}
              <div className="summary">
                <div className="summary-item">
                  <div className="summary-value">{selectedRestock.total_produits}</div>
                  <div className="summary-label">Produits</div>
                </div>
                <div className="summary-item">
                  <div className="summary-value">{selectedRestock.total_unites}</div>
                  <div className="summary-label">Unités</div>
                </div>
                <div className="summary-item">
                  <div className="summary-value">{parseFloat(selectedRestock.cout_total).toLocaleString()} XOF</div>
                  <div className="summary-label">Coût total</div>
                </div>
              </div>

              {/* Tableau */}
              <table>
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th className="text-center">Code</th>
                    <th className="text-center">Stock avant</th>
                    <th className="text-center">Qté ajoutée</th>
                    <th className="text-center">Stock après</th>
                    <th className="text-right">Prix unitaire</th>
                    <th className="text-right">Coût</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRestock.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.product_nom}</td>
                      <td className="text-center">{item.product_code || '-'}</td>
                      <td className="text-center">{item.stock_avant}</td>
                      <td className="text-center">+{item.quantite}</td>
                      <td className="text-center">{item.stock_apres}</td>
                      <td className="text-right">{parseFloat(item.prix_achat).toLocaleString()} XOF</td>
                      <td className="text-right">{parseFloat(item.cout_ligne).toLocaleString()} XOF</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan="6" className="text-right">TOTAL</td>
                    <td className="text-right">{parseFloat(selectedRestock.cout_total).toLocaleString()} XOF</td>
                  </tr>
                </tbody>
              </table>

              {selectedRestock.notes && (
                <div style={{ marginTop: '20px', padding: '10px', background: '#f3f4f6', borderRadius: '8px' }}>
                  <strong>Notes:</strong> {selectedRestock.notes}
                </div>
              )}

              {/* Pied de page */}
              <div className="footer">
                <p>Document généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}</p>
                {settings?.nom_entreprise && <p>© {new Date().getFullYear()} {settings.nom_entreprise}</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessRestockHistory;
